import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData, getPageType } from '../../services/seoContentServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { validateFile } from '../../utils/validators';

const DEFAULTS = {
  id: '', page_name: '', meta_title: '', meta_description: '',
  og_type: 'website', og_locale: 'en_US', og_site_name: '', og_url: '',
  twitter_card: 'summary', twitter_site: 'scientexevents',
  seo_image: null, uploadedfile: null,
};

const Seocontent = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState(DEFAULTS);
  const [modalType, setModalType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTypes, setPageTypes] = useState([{ value: "", label: "Select Page Name"}]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try{
      const data = await getData();
      setDataList(data);
    }
    catch (error) {
      console.error('Error fetching data:', error);
    }
    finally {
      setTimeout(() => { setLoading(false); }, 500);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const fetchPageType = async () => {
      try {
        const data = await getPageType();
        const formatedOptions = data?.map(item => ({ 'value': item.page_value, 'label': item.page_title }));
        setPageTypes(formatedOptions.sort((a,b) => a.value.localeCompare(b.value)));
      } catch (error) {
        console.error('Error fetching page type:', error);
      }
    };
    fetchPageType();
  }, []);

  const validateForm = () => {
    const newErrors = {}; 
    if (!form.page_name) newErrors.page_name = "Page Name is required";
    if (!form.meta_title) newErrors.meta_title = "Meta Title is required";
    if (!form.meta_description) newErrors.meta_description = "Meta Description is required";
    if (form.meta_title.length < 20 || form.meta_title.length > 70)
      newErrors.meta_title = "Meta Title should be between 20 and 70 characters";
    if (form.meta_description.length < 70 || form.meta_description.length > 160)
      newErrors.meta_description = "Meta Description should be between 70 and 160 characters";
    if (!form.og_site_name) newErrors.og_site_name = "OG Site Name is required";
    if (!form.og_url) newErrors.og_url = "OG URL is required";
    if (form.og_url && !/^https?:\/\/.+/.test(form.og_url))
      newErrors.og_url = "OG URL must start with http:// or https://";
    if (!form.uploadedfile && !form.seo_image)
      newErrors.uploadedfile = "SEO Image is required";

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys({ ...errors, ...newErrors }).every((key) => !errors[key] && !newErrors[key]);
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    const file = files?.[0];
    try {
      let updatedForm = { ...form };
      let updatedErrors = { ...errors };

      if (file) {
        const isValid = await validateFile(file, setErrors, name, 'img');
        if (!isValid) return;
        updatedForm[name] = file;
        updatedErrors[name] = '';
      } else {
        updatedForm[name] = value;
        if (value) updatedErrors[name] = '';
        if (name === 'page_name') {
          const isDuplicate = dataList.some(
            (item) => item.page_name?.toLowerCase() === value.toLowerCase()
          );
          updatedErrors[name] = isDuplicate ? "Page name already exists" : '';
        }
      }
      setForm(updatedForm);
      setErrors(updatedErrors);
    } catch (error) {
      console.error('Error in handleChange:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const fd = new FormData();
    fd.append('id', form.id);
    fd.append('page_name', form.page_name);
    fd.append('meta_title', form.meta_title);
    fd.append('meta_description', form.meta_description);
    fd.append('og_site_name', form.og_site_name);
    fd.append('og_url', form.og_url);
    if (form.uploadedfile) fd.append('uploadedfile', form.uploadedfile);

    const response = modalType === 'add' ? await addData(fd) : await updateData(fd);
    if (response.status === 200 || response.status === 201) {
      toast.success(modalType === 'add' ? "Added Successfully!" : "Updated Successfully!", { position: 'top-right', autoClose: 3000 });
    } else {
      toast.error(response.message || "Failed to process request.", { position: 'top-right', autoClose: 3000 });
    }
    fetchData();
    setShowForm(false);
  };

  const handleDelete = async () => {
    try{
      if (selectedData) {
        setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
        await deleteData(selectedData);
        setShowDeleteConfirmModal(false);
        toast.success("Deleted Successfully!", {position: 'top-right',autoClose: 3000});
      }
    }
    catch (error) {
      console.error('Error deleting data:', error);
    }
  };

  const openForm = (type, data = DEFAULTS) => {
    setModalType(type);
    setForm({ ...DEFAULTS, ...data });
    setShowForm(true);
    setErrors({});
  };

  const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange = (page) => setCurrentPage(page);

  const filteredData = useMemo(() => {
    const filtered = dataList.filter(
      (data) =>
        data.page_name.toLowerCase().includes(searchText.toLowerCase()) ||
        data.meta_title.toLowerCase().includes(searchText.toLowerCase()) ||
        data.meta_description.toLowerCase().includes(searchText.toLowerCase())
    );
    return [...filtered].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [dataList, searchText]);

  const columns = [
    { name: '#', selector: (row, index) => (currentPage - 1) * 10 + (index + 1), sortable: false, width: '5%' },
    {
      name: 'Page name',
      selector: (row) => {
        const matched = pageTypes.find(opt => opt.value === row.page_name);
        const label = matched ? matched.label : row.page_name;
        return <span title={label}>{label}</span>;
      },
      sortable: true, width:'15%'
    },
    { name: 'Meta title', selector: (row) => (<span title={row.meta_title}>{row.meta_title}</span>), sortable: true, width:'20%' },
    { name: 'Meta Description', cell: (row) => (<span title={row.meta_description}>{row.meta_description}</span>), sortable: true, width:'40%' },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button className="me-2" variant="warning" size="sm" onClick={() => openForm('edit', row)}> Edit </Button>
          {/* <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)}> Delete </Button> */}
        </div>
      ),
      width:'20%'
    },
  ];

  const customStyles = (hasError) => ({
    control: (provided, state) => ({
      ...provided,
      borderColor: hasError ? '#f5c2c7' : provided.borderColor,
      boxShadow: hasError ? '0 0 0 1px rgba(167, 16, 31, 0.91)' : state.isFocused ? '0 0 0 1px #2684FF' : provided.boxShadow,
      '&:hover': { borderColor: hasError ? '#f5c2c7' : provided.borderColor },
    }),
  });

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8"><p className='mb-3 fs-4'>SEO Config <b>/ Meta Tags</b></p></div>
                {!showForm && (
                  <div className="col-4 text-end">
                    <button className="btn btn-primary mb-3 form-label-custom" onClick={() => openForm('add')}>Add</button>
                  </div>
                )}
              </div>

              {!showForm ? (
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination search highlightOnHover responsive striped
                  paginationPerPage={10}
                  paginationComponentOptions={{ noRowsPerPage: true }}
                  onChangePage={handlePageChange}
                  subHeader
                  subHeaderComponent={
                    <input className="form-control" type="text" placeholder="Search" value={searchText} onChange={handleSearchChange} />
                  }
                />
              ) : (
                <div className="card p-4">
                  <h4 className='mb-4'>{modalType === 'add' ? 'Add' : 'Edit'} SEO Content</h4>
                  <Form>
                    <h6 className="text-muted mb-3">Section 1: Meta</h6>
                   <Form.Group className="mb-3">
                      <Form.Label className='form-label-custom'>Page Name<small className='text-danger'>*</small></Form.Label>
                      {modalType === 'add' ? (
                        <>
                          <Select
                            id="page_name"
                            name="page_name"
                            value={pageTypes.find(opt => opt.value === form.page_name)}
                            onChange={(selectedOption) =>
                              handleChange({ target: { name: 'page_name', value: selectedOption?.value || '' } })
                            }
                            options={pageTypes || []}
                            styles={customStyles(!!errors.page_name)}
                            placeholder="Select Page"
                          />
                          {errors.page_name && <div className="invalid-feedback d-block">{errors.page_name}</div>}
                        </>
                      ) : (
                        <Form.Control
                          type="text"
                          value={pageTypes.find(opt => opt.value === form.page_name)?.label || form.page_name}
                          readOnly
                          disabled
                          className="bg-light"
                        />
                      )}
                      </Form.Group>
                    <Form.Group className="mb-3"> 
                      <Form.Label className='form-label-custom'>Meta title <small className='text-danger'>*</small>
                        <small className='text-primary'> Char Limit: 20 -70 | Current Char Count: {form.meta_title.length}</small>
                      </Form.Label>
                      <Form.Control type="text" name="meta_title" value={form.meta_title} onChange={handleChange} isInvalid={!!errors.meta_title} />
                      <Form.Control.Feedback type="invalid">{errors.meta_title}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className='form-label-custom'>Meta description <small className='text-danger'>*</small>
                        <small className='text-primary'>Char Limit: 70 -160 | Current Char Count: {form.meta_description.length}</small>
                      </Form.Label>
                      <Form.Control as="textarea" rows={8} name="meta_description" value={form.meta_description} onChange={handleChange} isInvalid={!!errors.meta_description} />
                      <Form.Control.Feedback type="invalid">{errors.meta_description}</Form.Control.Feedback>
                    </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">Section 2: Open Graph Protocol</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>OG Type</Form.Label>
                      <Form.Control type="text" value={form.og_type} readOnly disabled className="bg-light" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>OG Locale</Form.Label>
                      <Form.Control type="text" value={form.og_locale} readOnly disabled className="bg-light" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>OG Site Name <small className='text-danger'>*</small></Form.Label>
                      <Form.Control type="text" name="og_site_name" value={form.og_site_name} onChange={handleChange} isInvalid={!!errors.og_site_name} />
                      <Form.Control.Feedback type="invalid">{errors.og_site_name}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>OG URL <small className='text-danger'>*</small></Form.Label>
                      <Form.Control type="text" name="og_url" value={form.og_url} onChange={handleChange}
                        placeholder="https://www.<subdomain>.scientexconference.com/<page-path>"
                        isInvalid={!!errors.og_url} />
                      <Form.Control.Feedback type="invalid">{errors.og_url}</Form.Control.Feedback>
                    </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">Section 3: Twitter Card</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>Twitter Card Type</Form.Label>
                      <Form.Control type="text" value={form.twitter_card} readOnly disabled className="bg-light" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Twitter Site</Form.Label>
                      <Form.Control type="text" value={form.twitter_site} readOnly disabled className="bg-light" />
                    </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">SEO Image (Image Dimension: 1200 x 630 | Kb: 10 - 150) <small className='text-danger'>*</small> (used for og:image and twitter:image)</h6>
                    <Form.Group className="mb-3">
                      <Form.Control type="file" name="uploadedfile" accept="image/*" onChange={handleChange} isInvalid={!!errors.uploadedfile} />
                      <Form.Control.Feedback type="invalid">{errors.uploadedfile}</Form.Control.Feedback>
                      {form.seo_image && !form.uploadedfile && (
                        <img src={form.seo_image} alt="Current SEO" style={{ maxWidth: 200, marginTop: 8 }} />
                      )}
                      {form.uploadedfile && (
                        <img src={URL.createObjectURL(form.uploadedfile)} alt="New SEO preview" style={{ maxWidth: 200, marginTop: 8 }} />
                      )}
                    </Form.Group>

                    <div className="mt-4">
                      <Button variant="secondary" className="me-2" onClick={() => setShowForm(false)}>Cancel</Button>
                      <Button variant="primary" onClick={handleSubmit}>{modalType === 'add' ? 'Add' : 'Save'}</Button>
                    </div>
                  </Form>
                </div>
              )}
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Seocontent;